import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { LanguageSwitcher } from '../../components/common';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ThemeColors } from '../../styles/ThemeStyles';

const LogoContainer = styled.div`
  margin-bottom: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const LogoImage = styled.img`
  width: 80%;
  height: auto;
`;

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f0f5ff;
  padding: 20px;
  position: relative;
`;

const LoginForm = styled.form`
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  padding: 30px 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const Title = styled.h2`
  margin-top: 16px;
  margin-bottom: 30px;
  color: ${ThemeColors.primary.main};
  text-align: center;
  font-size: 24px;
  font-weight: 600;
`;

const LanguageSwitcherContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 30px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px;
  margin-bottom: 20px;
  border: 1px solid #ddd;
  border-radius: 10px;
  box-sizing: border-box;
  font-size: 16px; /* Prevents zoom on iOS */
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px rgba(26, 66, 138, 0.2);
  }
`;

const PasswordContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 20px;
`;

const PasswordToggleButton = styled.button`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  padding: 8px;
  
  &:hover {
    color: #333;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
`;

const Checkbox = styled.input`
  margin-right: 10px;
  cursor: pointer;
  accent-color: ${ThemeColors.primary.main};
  width: 18px;
  height: 18px;
`;

const CheckboxLabel = styled.label`
  font-size: 15px;
  color: #2D2123;
  cursor: pointer;
`;

const Button = styled.button`
  width: 100%;
  padding: 16px;
  background-color: ${ThemeColors.primary.main};
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 18px;
  font-weight: 500;
  transition: background-color 0.2s;
  margin-top: 10px;

  &:hover {
    background-color: ${ThemeColors.primary.dark};
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  color: #F44336;
  margin-top: 15px;
  text-align: center;
  font-size: 16px;
`;

export const LoginScreenMobile = () => {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { signIn, loading, error, user, userRole } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Get redirect from URL params first, then fall back to sessionStorage
  const redirectTo = searchParams.get('redirect') || sessionStorage.getItem('loginRedirect');

  // Store redirect in sessionStorage if present in URL params
  useEffect(() => {
    if (redirectTo && redirectTo.startsWith('/')) {
      sessionStorage.setItem('loginRedirect', redirectTo);
    }
  }, [redirectTo]);

  // Redirect after successful login - let ManagerNavigator handle the actual redirect
  // This just ensures we navigate away from login screen
  useEffect(() => {
    if (user && userRole) {
      console.log("User logged in, RootNavigator will switch navigators");
      // The RootNavigator will switch to ManagerNavigator/SalesRepNavigator
      // which will handle the redirect via LoginRedirectHandler
    }
  }, [user, userRole]);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      // Pass rememberMe flag to signIn function - supports username or email
      await signIn(usernameOrEmail, password, rememberMe);
    } catch (e) {
      // Error is already handled by useAuth and set in the context
    }
  };
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <LoginContainer>
      <LoginForm onSubmit={handleLogin}>
        <LanguageSwitcherContainer>
          <LanguageSwitcher variant="compact" />
        </LanguageSwitcherContainer>
        <LogoContainer>
          <LogoImage src="/assets/erp-logo.svg" alt="ERP Mock Logo" />
        </LogoContainer>
        <Title>{t('auth.portalLogin')}</Title>
        <Input
          type="text"
          placeholder={t('auth.usernameOrEmail', 'Username or Email')}
          value={usernameOrEmail}
          onChange={(e) => setUsernameOrEmail(e.target.value)}
          required
        />
        <PasswordContainer>
          <Input
            type={showPassword ? "text" : "password"}
            placeholder={t('auth.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <PasswordToggleButton 
            type="button" 
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? t('auth.hidePassword', 'Hide password') : t('auth.showPassword', 'Show password')}
          >
            {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
          </PasswordToggleButton>
        </PasswordContainer>
        <CheckboxContainer>
          <Checkbox 
            type="checkbox" 
            id="rememberMeMobile" 
            checked={rememberMe} 
            onChange={(e) => setRememberMe(e.target.checked)} 
          />
          <CheckboxLabel htmlFor="rememberMeMobile">
            {t('auth.rememberMe', 'Remember me')}
          </CheckboxLabel>
        </CheckboxContainer>
        <Button type="submit" disabled={loading}>
          {loading ? t('auth.loggingIn') : t('auth.login')}
        </Button>
        {error && <ErrorText>{error}</ErrorText>}
      </LoginForm>
    </LoginContainer>
  );
};
